import { BallotVariant } from '@/materials/color'
import * as Sentry from '@sentry/browser'
import * as t from 'io-ts'
import { parseMultiLocaleArticleIds } from './article-ids'
import { BallotState, caseBallotState, mkBallotState, __BallotState } from './ballot-state'
import { CantonState, lookupCanton, mkCantonId, __CantonState } from './cantons'
import { BallotStateDocument, FederalBallotDocument, isTiebreakQuestion } from './documents'
import {
  FederalBallotState,
  mkFederalBallotState,
  mkMajorityOfCantonsVote,
  __FederalBallotState,
} from './federal-ballot-state'
import { MultiLocaleString } from './multi-locale-string'
import { caseOutcome } from './outcome'
import { casePopularVoteVisibility, PopularVoteVisibility } from './popular-vote-visibility'
import { resultOutcome } from './result'
import { caseTrend } from './trend'
import firestore from 'firebase/firestore'
import * as F from 'fp-ts'

/**
 * Summary of a federal ballot, in the form that is consumed by many UI
 * components. Objects of this type are created from the raw data in the
 * database, by inspecting what data is available and in what state the ballot
 * is on the federal and cantonal levels.
 */
export interface FederalBallotSummary {
  /**
   * The Federal Ballot ID (integer, starting at 1, as string).
   */
  id: string

  /**
   * The date (day) when the ballot was held. Use only the date part, not the
   * time. This field is parsed from the 'popularVoteId' ("YYYY-MM-DD").
   */
  date: Date

  variant: BallotVariant

  name: MultiLocaleString
  detailName: MultiLocaleString

  lead: MultiLocaleString
  majorityOfCantonsRequired: boolean
  articleIds: { [k: string]: string[] }

  /**
   * BallotState at the federal level.
   */
  state: FederalBallotState

  /**
   * BallotState in individual cantons.
   */
  cantons: Array<CantonState>

  /**
   * The 'externalSource' field from 'FederalBallotDocument'. The 'vorlagenId'
   * is needed for the gauge charts, which need it to fetch the data directly
   * from a third-party API.
   */
  externalSource: FederalBallotDocument['externalSource']
}

export const mkFederalBallotSummary = (x: FederalBallotSummary) => x

// ----------------------------------------------------------------------------
// Constructing a FederalBallotSummary out of database snapshots.

interface Input {
  date: Date
  visibility: PopularVoteVisibility
  // federalBallots/<id>
  docSnapshot: undefined | firestore.DocumentSnapshot
  // federalBallots/<id>/cantons
  cantonsSnapshot: undefined | firestore.QuerySnapshot
}

export function toFederalBallotSummary(input: Input): undefined | FederalBallotSummary {
  const { date, visibility, docSnapshot, cantonsSnapshot } = input

  if (!docSnapshot || !cantonsSnapshot) {
    return undefined
  }

  const docData = docSnapshot.data()
  if (!docData) {
    return undefined
  }

  const majorityOfCantonsRequired = F.function.pipe(
    t.boolean.decode(docData.majorityOfCantonsRequired),
    F.either.getOrElseW(() => false),
  )

  const pre = mkBallotState('pre')
  const docToState = (data: firestore.DocumentData): BallotState =>
    casePopularVoteVisibility({
      inactive: pre,
      preview: pre,
      visible: docToBallotState(data || {}),
    })(visibility)

  const cantons = cantonsSnapshot.docs.map((c) => {
    const cantonData = c.data()

    return {
      id: mkCantonId(c.id),
      state: docToState(cantonData),
      info: F.function.pipe(
        MultiLocaleString.decode(cantonData.info),
        F.either.getOrElseW(() => ({})),
      ),
    }
  })

  const federalBallotState: FederalBallotState = (() => {
    const ballotState = docToState(docData)
    if (!majorityOfCantonsRequired) {
      return mkFederalBallotState({
        ballotState,
        majorityOfCantons: mkMajorityOfCantonsVote('notRequired'),
      })
    } else {
      const majorityOfCantons = (() => {
        const cantonWeights = cantons.reduce(
          (a, cs) =>
            caseBallotState({
              pre: a,
              inProgress: a,
              trend: () => a,
              projection: () => a,
              finalResult: ({ finalResult }) =>
                caseOutcome({
                  yea: { yea: a.yea + lookupCanton(cs.id).weight, nay: a.nay },
                  nay: { yea: a.yea, nay: a.nay + lookupCanton(cs.id).weight },
                })(resultOutcome(finalResult)),
            })(cs.state),
          { yea: 0, nay: 0 },
        )

        if (cantonWeights.nay >= 11.5) {
          return mkMajorityOfCantonsVote('reject')
        } else if (cantonWeights.yea >= 12) {
          return mkMajorityOfCantonsVote('accept')
        } else {
          return mkMajorityOfCantonsVote('undecided')
        }
      })()

      return mkFederalBallotState({ ballotState, majorityOfCantons })
    }
  })()

  const articleIds = parseMultiLocaleArticleIds(
    F.function.pipe(
      MultiLocaleString.decode(docData.articleIds),
      F.either.getOrElseW(() => ({})),
    ),
  )

  const externalSource = F.function.pipe(
    FederalBallotDocument.decode(docData),
    F.either.map((x) => x.externalSource as undefined | typeof x.externalSource),
    F.either.getOrElseW(() => undefined),
  )

  if (!externalSource) {
    return undefined
  }

  return {
    id: docSnapshot.id,

    date,
    variant: isTiebreakQuestion(docSnapshot.id, docSnapshot.data() as any) ? 'tieBreak' : 'default',

    name: F.function.pipe(
      MultiLocaleString.decode(docData.name),
      F.either.getOrElseW(() => ({})),
    ),
    detailName: F.function.pipe(
      MultiLocaleString.decode(docData.detailName),
      F.either.getOrElseW(() => ({})),
    ),

    lead: F.function.pipe(
      MultiLocaleString.decode(docData.lead),
      F.either.getOrElseW(() => ({})),
    ),

    majorityOfCantonsRequired,
    articleIds,
    state: federalBallotState,
    cantons,

    externalSource,
  }
}

export const docToBallotState = (doc: firestore.DocumentData): BallotState => {
  const failure = t.failure<BallotState>('', [])

  return F.function.pipe(
    BallotStateDocument.decode(doc),
    F.either.fold(
      () => {
        /*
         * This shouldn't happen. We treat this as error and send a message
         * to Sentry so we can investigate.
         */
        Sentry.withScope((scope) => {
          scope.setExtras({ doc })
          Sentry.captureMessage(`docToBallotState: failed to decode document`)
        })

        return mkBallotState('in-progress')
      },
      ({ trend, projection, result }) => {
        const errOrTrend = (() => {
          if (trend) {
            return caseTrend({
              Unknown: failure,
              Yea: t.success(mkBallotState({ trend: 'yea', at: trend.at || undefined })),
              Nay: t.success(mkBallotState({ trend: 'nay', at: trend.at || undefined })),
            })(trend.trend)
          } else {
            return failure
          }
        })()

        const errOrProjection = (() => {
          if (projection && projection.votes.yea !== null) {
            return t.success(mkBallotState({ projection: projection.votes.yea, detail: projection.detail || {} }))
          } else {
            return failure
          }
        })()

        const errOrResult = (() => {
          if (result && result.votes.yea.percent !== null && result.votes.nay.percent !== null) {
            const {
              accepted = null,
              voterTurnout,
              votes: { yea, nay },
            } = result

            return t.success(
              mkBallotState({
                finalResult: {
                  accepted,
                  voterTurnout,
                  votes: {
                    yea: { ...yea, percent: result.votes.yea.percent },
                    nay: { ...nay, percent: result.votes.nay.percent },
                  },
                },
              }),
            )
          } else {
            return failure
          }
        })()

        return F.function.pipe(
          errOrResult,
          F.either.alt(() => errOrProjection),
          F.either.alt(() => errOrTrend),
          F.either.getOrElse(() => mkBallotState('in-progress')),
        )
      },
    ),
  )
}

// ----------------------------------------------------------------------------
// Catalog

export const __FederalBallotSummary = {
  pre: mkFederalBallotSummary({
    id: '2018-03-04',
    date: new Date(Date.parse('2018-03-04')),
    variant: 'default',
    name: { de: 'Abschaffung der Billag-Gebühren' },
    detailName: { de: 'Abschaffung der Billag-Gebühren' },
    lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
    majorityOfCantonsRequired: true,
    articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

    state: __FederalBallotState.pre,
    cantons: __CantonState.pre,

    externalSource: { vorlagenId: 6410 as t.Int, name: {} },
  }),
  inProgress: mkFederalBallotSummary({
    id: '2018-03-04',
    date: new Date(Date.parse('2018-03-04')),
    variant: 'default',
    name: { de: 'Abschaffung der Billag-Gebühren' },
    detailName: { de: 'Abschaffung der Billag-Gebühren' },
    lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
    majorityOfCantonsRequired: true,
    articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

    state: __FederalBallotState.inProgress,
    cantons: __CantonState.mixed,

    externalSource: { vorlagenId: 6410 as t.Int, name: {} },
  }),
  trend: mkFederalBallotSummary({
    id: '2018-03-04',
    date: new Date(Date.parse('2018-03-04')),
    variant: 'default',
    name: { de: 'Abschaffung der Billag-Gebühren' },
    detailName: { de: 'Abschaffung der Billag-Gebühren' },
    lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
    majorityOfCantonsRequired: true,
    articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

    state: __FederalBallotState.trend.yea,
    cantons: __CantonState.trend,

    externalSource: { vorlagenId: 6410 as t.Int, name: {} },
  }),
  projection: mkFederalBallotSummary({
    id: '2018-03-04',
    date: new Date(Date.parse('2018-03-04')),
    variant: 'default',
    name: { de: 'Abschaffung der Billag-Gebühren' },
    detailName: { de: 'Abschaffung der Billag-Gebühren' },
    lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
    majorityOfCantonsRequired: true,
    articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

    state: __FederalBallotState.projection.yea,
    cantons: __CantonState.projection,

    externalSource: { vorlagenId: 6470 as t.Int, name: {} },
  }),
  finalResult: {
    yea: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: true,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.finalResult.yea,
      cantons: __CantonState.finalResult,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    nay: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: true,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.finalResult.nay,
      cantons: __CantonState.finalResult,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
  },
  majorityOfCantonsReject: {
    yea: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: true,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.majorityOfCantonsReject.yea,
      cantons: __CantonState.finalResult.map((x) => ({ ...x, state: __BallotState.finalResult.nay })),

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    nay: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: true,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.majorityOfCantonsReject.nay,
      cantons: __CantonState.finalResult.map((x) => ({ ...x, state: __BallotState.finalResult.nay })),

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
  },
  popularMajority: {
    inProgress: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: false,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.inProgress,
      cantons: __CantonState.inProgress,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    trend: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: false,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.trend.yea,
      cantons: __CantonState.trend,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    projection: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: false,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.projection.yea,
      cantons: __CantonState.projection,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    finalResult: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: false,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.finalResult.yea,
      cantons: __CantonState.finalResult,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
    finalResultWithGap: mkFederalBallotSummary({
      id: '2018-03-04',
      date: new Date(Date.parse('2018-03-04')),
      variant: 'default',
      name: { de: 'Abschaffung der Billag-Gebühren' },
      detailName: { de: 'Abschaffung der Billag-Gebühren' },
      lead: { de: 'Hier ein kurzer Text um was es geht an diesem Tag und was so ansteht.' },
      majorityOfCantonsRequired: false,
      articleIds: { de: ['15547519', '26044116', '23677673', '25875737', '30567861'] },

      state: __FederalBallotState.finalResult.yeaWithGap,
      cantons: __CantonState.finalResult,

      externalSource: { vorlagenId: 6410 as t.Int, name: {} },
    }),
  },
}
